import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography } from
"@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTenantHost } from "app/tenant/TenantHostProvider";
import { resolveLogoSrc } from "app/tenant/tenantBranding";
import {
  defaultVisitTypeForDate,
  departmentLabel,
  displayConsultationFee,
  formatConsultationFee,
  isCampAvailableForDate,
  requiredBookingFormErrors,
  resolveDoctorDepartment } from
"./publicTokenBookingForm";
import {
  fetchPublicTokenCatalog,
  publicTokenErrorMessage,
  submitPublicTokenRegistration } from
"./publicTokenBookingApi";

const EMPTY_FORM = {
  visitDate: "",
  doctorId: "",
  visitTypeCode: "NEW",
  fullName: "",
  mobile: "",
  dob: "",
  gender: "",
  caretakerName: "",
  caretakerMobile: "",
  caretakerRelation: ""
};

export default function PublicTokenBookingPage() {
  const tenant = useTenantHost();
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const hospitalName = catalog?.hospitalName || tenant.hospitalName || "Hospital";
  const availableDates = catalog?.availableDates || [];
  const doctors = useMemo(() => catalog?.doctors || [], [catalog?.doctors]);
  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.id === form.doctorId) || null,
    [doctors, form.doctorId]
  );
  const selectedDepartment = useMemo(
    () => resolveDoctorDepartment(selectedDoctor),
    [selectedDoctor]
  );
  const campAvailable = isCampAvailableForDate(catalog, form.visitDate);
  const displayedFee = displayConsultationFee(selectedDepartment, form.visitTypeCode);
  const feeLabel = formatConsultationFee(displayedFee);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = () => {
      setCatalogLoading(true);
      return fetchPublicTokenCatalog(host)
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setCatalogError(null);
        }
        return data;
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalogError(publicTokenErrorMessage(err));
        }
        return null;
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    };
    loadCatalog();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadCatalog();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [host]);

  const logoSrc = resolveLogoSrc({
    logoUrl: tenant.logoUrl,
    hasLogo: tenant.hasLogo
  });

  const onChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      if (field === "visitDate") {
        return {
          ...prev,
          visitDate: value,
          visitTypeCode: defaultVisitTypeForDate(catalog, value)
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const errors = requiredBookingFormErrors(form, availableDates);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await submitPublicTokenRegistration(host, {
        visitDate: form.visitDate,
        doctorId: form.doctorId,
        visTypeCode: form.visitTypeCode,
        patient: {
          fullName: form.fullName.trim(),
          primaryMobile: form.mobile.trim(),
          dob: form.dob,
          gender: form.gender,
          caretakerName: form.caretakerName.trim() || undefined,
          caretakerMobile: form.caretakerMobile.trim() || undefined,
          caretakerRelation: form.caretakerRelation.trim() || undefined
        }
      });
      setResult(data);
    } catch (err) {
      const code = err?.response?.data?.error;
      setSubmitError(publicTokenErrorMessage(err));
      if (code === "BOOKING_DATE_UNAVAILABLE") {
        fetchPublicTokenCatalog(host)
        .then((data) => {
          setCatalog(data);
          const stillValid = (data?.availableDates || []).some((d) => d.date === form.visitDate);
          if (!stillValid) {
            setForm((prev) => ({ ...prev, visitDate: "", visitTypeCode: defaultVisitTypeForDate() }));
          }
        })
        .catch(() => {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenant.isTenantHost) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", px: 2 }}>
        <Typography sx={{ textAlign: "center", color: "#64748B" }}>
          Open this page from your hospital URL to book a token.
        </Typography>
      </Box>);

  }

  if (result) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", px: 2, py: 4 }}>
        <Stack spacing={2} sx={{ maxWidth: 420, mx: "auto", textAlign: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Token Booked Successfully
          </Typography>
          <Typography sx={{ color: "#475569" }}>
            Hospital:
            <br />
            {result.hospitalName || hospitalName}
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            Date:
            <br />
            {result.visitDateLabel || result.visitDate}
          </Typography>
          {result.doctorName &&
          <Typography sx={{ color: "#334155" }}>
              Doctor:
              <br />
              {result.doctorName}
            </Typography>
          }
          {result.departmentLabel &&
          <Typography sx={{ color: "#64748B" }}>
              Department:
              <br />
              {result.departmentLabel}
            </Typography>
          }
          <Typography sx={{ color: "#64748B", letterSpacing: 1 }}>Your Token</Typography>
          <Typography
            data-testid="booking-token"
            sx={{ fontSize: 56, fontWeight: 900, lineHeight: 1, color: "#0F172A" }}>

            {result.tokenNumber != null ? result.tokenNumber : result.tokenDisplay}
          </Typography>
          {result.tokenDisplay &&
          <Typography sx={{ color: "#94A3B8" }}>{result.tokenDisplay}</Typography>
          }
          <Typography sx={{ color: "#475569" }}>
            Please arrive at the hospital before your token is called.
          </Typography>
        </Stack>
      </Box>);

  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", px: 2, py: 3 }}>
      <Stack component="form" onSubmit={onSubmit} spacing={2.5} sx={{ maxWidth: 420, mx: "auto" }}>
        <Stack alignItems="center" spacing={1}>
          {tenant.hasLogo &&
          <Box component="img" src={logoSrc} alt={hospitalName} sx={{ height: 56, objectFit: "contain" }} />
          }
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {hospitalName}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Book Your Token
          </Typography>
          <Typography sx={{ color: "#64748B", textAlign: "center" }}>
            Enter your details to book a token for your hospital visit.
          </Typography>
        </Stack>

        {catalogLoading &&
        <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        }
        {catalogError && <Alert severity="error">{catalogError}</Alert>}

        {!catalogLoading &&
        <>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Your Details</Typography>
              <Stack spacing={1.5}>
                <TextField
                label="Name"
                value={form.fullName}
                onChange={onChange("fullName")}
                error={Boolean(fieldErrors.fullName)}
                helperText={
                fieldErrors.fullName || "Enter the name of the person visiting the hospital."
                }
                inputProps={{ "aria-label": "Name" }}
                fullWidth
                required />

                <TextField
                label="Mobile Number"
                value={form.mobile}
                onChange={onChange("mobile")}
                error={Boolean(fieldErrors.mobile)}
                helperText={fieldErrors.mobile}
                inputProps={{ inputMode: "numeric", maxLength: 10, "aria-label": "Mobile Number" }}
                fullWidth
                required />

                <TextField
                label="Date of Birth"
                type="date"
                value={form.dob}
                onChange={onChange("dob")}
                error={Boolean(fieldErrors.dob)}
                helperText={fieldErrors.dob}
                InputLabelProps={{ shrink: true }}
                inputProps={{ "aria-label": "Date of Birth" }}
                fullWidth
                required />

                <FormControl fullWidth error={Boolean(fieldErrors.gender)}>
                  <InputLabel id="camp-gender-label">Gender</InputLabel>
                  <Select
                  labelId="camp-gender-label"
                  label="Gender"
                  value={form.gender}
                  onChange={onChange("gender")}
                  inputProps={{ "aria-label": "Gender" }}>

                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="O">Other</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <FormControl fullWidth error={Boolean(fieldErrors.visitDate)}>
              <InputLabel id="public-date-label">Date</InputLabel>
              <Select
              labelId="public-date-label"
              label="Date"
              value={form.visitDate}
              onChange={onChange("visitDate")}
              inputProps={{ "aria-label": "Date" }}>

                {availableDates.map((item) =>
              <MenuItem key={item.date} value={item.date}>
                    {item.label || item.date}
                  </MenuItem>
              )}
              </Select>
              {fieldErrors.visitDate &&
            <Typography color="error" variant="caption">
                  {fieldErrors.visitDate}
                </Typography>
            }
            </FormControl>

            <FormControl fullWidth error={Boolean(fieldErrors.doctorId)}>
              <InputLabel id="public-doctor-label">Choose Doctor</InputLabel>
              <Select
              labelId="public-doctor-label"
              label="Choose Doctor"
              value={form.doctorId}
              onChange={onChange("doctorId")}
              inputProps={{ "aria-label": "Choose Doctor" }}>

                {doctors.map((doctor) =>
              <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </MenuItem>
              )}
              </Select>
            </FormControl>

            {selectedDoctor &&
          <Typography data-testid="public-department">
                Department: {departmentLabel(selectedDepartment) || "—"}
              </Typography>
          }

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Visit Type</Typography>
              <RadioGroup
              name="visitType"
              value={form.visitTypeCode}
              onChange={onChange("visitTypeCode")}>

                <FormControlLabel
                value="NEW"
                control={<Radio />}
                label="New Consultation" />

                {campAvailable &&
              <FormControlLabel
                value="CAMP"
                control={<Radio />}
                label="Medical Camp" />

              }
              </RadioGroup>
            </Box>

            {feeLabel != null &&
          <Typography data-testid="consultation-fee" sx={{ fontWeight: 800 }}>
                Consultation Fee: {feeLabel}
              </Typography>
          }

            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>Optional Caretaker</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <TextField label="Caretaker Name" value={form.caretakerName} onChange={onChange("caretakerName")} />
                  <TextField
                  label="Caretaker Mobile"
                  value={form.caretakerMobile}
                  onChange={onChange("caretakerMobile")} />

                  <TextField
                  label="Relationship"
                  value={form.caretakerRelation}
                  onChange={onChange("caretakerRelation")} />

                </Stack>
              </AccordionDetails>
            </Accordion>

            {submitError && <Alert severity="error">{submitError}</Alert>}

            <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{ py: 1.4, fontWeight: 800, textTransform: "none" }}>

              {submitting ? "Generating your token…" : "Generate My Token"}
            </Button>
          </>
        }
      </Stack>
    </Box>);

}